import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import {KeyboardDatePicker} from '@material-ui/pickers';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import * as IWLActions from 'ApplicationEntry/ducks/ProductsRiders/iwl';
import * as ErrorMessages from 'ApplicationEntry/components/WorkFlow/ErrorMessages';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';
import * as PremiumActions from 'ApplicationEntry/ducks/premium';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import * as BA from 'ApplicationEntry/models/BillingAgent';
import {matchCaseNumber} from 'ApplicationEntry/models/CaseNumber';
import {matchFormNumber} from 'ApplicationEntry/models/FormNumber';
import * as MA from 'ApplicationEntry/models/ModalApplication';
import States from 'ApplicationEntry/models/States';
import {
  isEmptyZipCode,
  isValidZipCode,
  ZipCode,
  unmaskZipCode,
  maskZipCode,
  ValidZipCode,
  EmptyZipCode,
} from 'ApplicationEntry/models/ZipCode';
import './../WorkFlow.sass';
import Agents from './Agents';
import './BillingAgent.sass';
import './../CaseDetailsSection.sass';
import Alert from 'Shared/components/Alert';
import {changeCalculatedPremium, changePremium} from 'ApplicationEntry/ducks/ProductsRiders/aip';
import {ApplicationModeType} from 'ApplicationEntry/models/ApplicationModeType';
import {isValidCaseDetails} from 'ApplicationEntry/models/CaseDetails';
import {isLoadedPolicyTypes} from 'ApplicationEntry/models/PolicyTypes';
import {ProductRiderGroups, ProductsRidersPolicyType} from 'ApplicationEntry/models/ProductsRiders/Families';
import {ValidGroupCriticalIllness} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/GroupCriticalIllness';
import useBuildPremiumParameterAutoFill from '../ProductsRiders/Group/useBuildPremiumParameterAutoFill';
import {ValidGroupCancer} from 'ApplicationEntry/models/ProductsRiders/Cancer/GroupCancer';
import {ValidGroupAccident} from 'ApplicationEntry/models/ProductsRiders/Accident/GroupAccident';
import {ValidGroupShop} from 'ApplicationEntry/models/ProductsRiders/Shop/GroupShop';
import {ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {isLoaded, PackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {checkCaseNumberBillingMode} from 'ApplicationEntry/ducks/billingAgent';
import {Plan, Product} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {AccountType} from 'ApplicationEntry/models/AccountType';
import { not } from 'fp-ts/lib/function';
import { isValidDY } from 'ApplicationEntry/models/Date';
import { getAchAccounType } from 'ApplicationEntry/models/GroupBillingAgent';
import { getPACData } from 'ApplicationEntry/api/awdApi';

type BillingAgentSectionProps = {
  modalApplication: MA.InvalidModalApplication | MA.ValidModalApplication;
};

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);

const getZipErrorMessage = (zip: ZipCode) =>
  zip.value !== '' && !isValidZipCode(zip) ? ErrorMessages.INVALID_ZIP_FORMAT : null;
const isInvalidDate = (date: Date) => isNaN(date.getTime());

const BillingAgentSection = ({modalApplication}: BillingAgentSectionProps) => {
  const {application} = modalApplication;
  const {criticalIllness, applicantDemographics, cancer, accident, shop, caseDetails} = application;
  let {
    packageSourceList,
    packageSource,
    productType,
    planType: CriticalIllnessPlanType,
    insuredSC,
    spouseSC,
  } = criticalIllness as ValidGroupCriticalIllness;
  let {
    packageList: cancerPackageList,
    packageName: cancerPackageName,
    plan: cancerPlan,
    planType: CancerPlanType,
  } = cancer as ValidGroupCancer;

  let {
    packageList: accidentPackageList,
    packageName: accidentPackgaeName,
    plan: accidentPlan,
    planType: accidentPlanType,
    productType: accidentProductType,
  } = accident as ValidGroupAccident;

  let {
    packageList: shopPackageList,
    packageOption: shopPackageName,
    plan: shopPlan,
    planType: shopPlanType,
  } = shop as ValidGroupShop;

  let {billingCode, situsState, notACH} = caseDetails as ValidGroupCaseDetails;

  const {dateOfBirth} = applicantDemographics;
  const stateList = States;
  const [ZipBlurred, setBlurZip] = useState(false);
  const [billingModeLabel, setBillingModeLabel] = useState<HTMLLabelElement | null>();
  const [issueDateBlurred, setBlurIssueDate] = useState(false);
  const [dateSignedBlurred, setBlurDateSigned] = useState(false);
  const [draftDayBlurred] = useState(false);
  const [signatureStateLabel, setSignatureStateLabel] = useState<HTMLLabelElement | null>();
  const [stateLabel, setStateLabel] = useState<HTMLLabelElement | null>();

  const isFormNumberSpecialChar = (formNumber: string): boolean => {
    var format = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/;
    return format.test(formNumber) ? true : false;
  };

  const showSpecialCharactersinFormNumber = isFormNumberSpecialChar(
    application.billingAgent?.formNumber.key === 'UserAssignedFormNumber'
      ? application.billingAgent?.formNumber.value
      : ''
  );

  const showIssueDateError =
    issueDateBlurred && application.billingAgent.issueDate !== '' && isInvalidDate(application.billingAgent.issueDate);

  const showDateSignedError =
    dateSignedBlurred &&
    application.billingAgent.dateSigned !== '' &&
    isInvalidDate(application.billingAgent.dateSigned);

  const showDaYError = application.billingAgent.achDraftDay.value !== '' && (parseInt(application.billingAgent.achDraftDay.value) <= 0 || parseInt(application.billingAgent.achDraftDay.value) > 31);

  const getApplicationType = BA.matchBillingAgentByType({
    Group: () => ApplicationType.Group,
    Ordinary: () => ApplicationType.Ordinary,
    NewYork: () => ApplicationType.NewYork,
  });
  const applicationType = getApplicationType(application.billingAgent);
  const dispatch = useDispatch();

  const showZipError = !isEmptyZipCode(application.billingAgent.zip) && !isValidZipCode(application.billingAgent.zip);

  const initialValue =
    modalApplication.mode === ApplicationModeType.Quality
      ? application.billingAgent.payroll
        ? 'payroll'
        : application.billingAgent.pac
        ? 'pac'
        : application.billingAgent.directBill
        ? 'directbill'
        : ''
      : 'payroll';

  const [value, setValue] = React.useState(initialValue);
  const [payrollDisabled, setPayrollDisabled] = React.useState(false);
  const [pacDisabled, setPACDisabled] = React.useState(true);
  const [payrollPACDisabled, setPayrollPACDisabled] = React.useState(false);
  const [handleBillingModeReload, setBillingModeReload] = React.useState(true);
  const [accountTypeLabel, setAccountTypeLabel] = useState<HTMLLabelElement | null>();

  const productGroupName =
    application.caseDetails.productType.key === 'LoadedPolicyTypes' &&
    application.caseDetails.productType.selectedPolicyTypes[0]?.friendlyName?.toLocaleLowerCase();

  const selectedProductType =
    application.caseDetails.productType.key === 'LoadedPolicyTypes' &&
    application.caseDetails.productType.selectedPolicyTypes;

  const handleAutoFillPremiumGrpProducts = useBuildPremiumParameterAutoFill();

  const handleBillingMode = (event: any, packageSourceList?: PackageModel) => {
    let productType: Product[] = [];
    if (isValidCaseDetails(application.caseDetails) && isLoadedPolicyTypes(application.caseDetails.productType)) {
      productType = application.caseDetails.productType.selectedPolicyTypes;
    }
    dispatch(BAActions.changeBillingMode(event.target.value as string));
    dispatch(PremiumActions.changeBillMode(event.target.value as string));

    let billingModePay = application.billingModes.billingModes
      .flatMap(m => m.occurrences)
      .find(o => o.name.toUpperCase() === event.target.value.toUpperCase())?.frequency!;

    productType.forEach(product => {
      if (product.productGroup!.toUpperCase() === ProductRiderGroups[ProductsRidersPolicyType.Aip]) {
        if (
          (application.aip.packageOption.value === 'A' ||
            application.aip.packageOption.value === 'B' ||
            application.aip.packageOption.value === 'C' ||
            application.aip.packageOption.value === 'D') &&
          application.aip.planType.value === 'IND'
        ) {
          if (
            application.aip.indMonthlyPremium.key === 'NonEmpty' ||
            application.aip.indWeeklyPremium.key === 'NonEmpty'
          ) {
            const premiumValue =
              billingModePay === 12 ? application.aip.indMonthlyPremium.value : application.aip.indWeeklyPremium.value;
            const prem = Number(premiumValue) * billingModePay;
            const newPremiumValue = parseFloat(prem.toString()).toFixed(2);

            dispatch(changePremium(premiumValue));
            dispatch(changeCalculatedPremium(newPremiumValue));
          }
        } else if (
          (application.aip.packageOption.value === 'A' ||
            application.aip.packageOption.value === 'B' ||
            application.aip.packageOption.value === 'C' ||
            application.aip.packageOption.value === 'D') &&
          application.aip.planType.value === 'FAMILY'
        ) {
          if (
            application.aip.familyMonthlyPremium.key === 'NonEmpty' ||
            application.aip.familyWeeklyPremium.key === 'NonEmpty'
          ) {
            const premiumValue =
              billingModePay === 12
                ? application.aip.familyMonthlyPremium.value
                : application.aip.familyWeeklyPremium.value;
            const prem = Number(premiumValue) * billingModePay;
            const newPremiumValue = parseFloat(prem.toString()).toFixed(2);
            dispatch(changePremium(premiumValue));
            dispatch(changeCalculatedPremium(newPremiumValue));
          }
        }
      }
      if (product.productGroup!.toUpperCase() === ProductRiderGroups[ProductsRidersPolicyType.Cancer]) {
        if (applicationType === ApplicationType.Group && productGroupName === 'group_cancer') {
          handleAutoFillPremium(packageSourceList!, NonEmptyString.create(event.target.value) as StringUnion);
        }
      }
      if (product.productGroup!.toUpperCase() === ProductRiderGroups[ProductsRidersPolicyType.Accident]) {
        if (applicationType === ApplicationType.Group && productGroupName === 'group_acc') {
          handleAutoFillPremium(packageSourceList!, NonEmptyString.create(event.target.value) as StringUnion);
        }
      }
      if (product.productGroup!.toUpperCase() === ProductRiderGroups[ProductsRidersPolicyType.Shop]) {
        if (applicationType === ApplicationType.Group && productGroupName === 'group_shop') {
          handleAutoFillPremium(packageSourceList!, NonEmptyString.create(event.target.value) as StringUnion);
        }
      }
      if (product.productGroup!.toUpperCase() === ProductRiderGroups[ProductsRidersPolicyType.CriticalIllness]) {
        if (applicationType === ApplicationType.Group && productGroupName === 'group_ci') {
          handleAutoFillPremium(packageSourceList!, NonEmptyString.create(event.target.value) as StringUnion);
        }
      }
    });
  };

  const handleAutoFillPremium = (packageSourceList: PackageModel, billingModeValue: StringUnion) => {
    let packageItem: object | null | undefined;
    let planType: StringUnion = NonEmptyString.create('');
    let plan: StringUnion = NonEmptyString.create('');

    if (
      productType.value === 'GVCIFX' ||
      productType.value === 'GVCIP1' ||
      productType.value === 'GVCIP2' ||
      productType.value === 'GVCIP4'
    ) {
      if (packageSourceList !== undefined && packageSourceList !== null) {
        packageItem = isLoaded(packageSourceList)
          ? packageSourceList.Data.find((packageItem: object) => packageItem['packageName'] === packageSource.value)
          : null;
      }

      planType = CriticalIllnessPlanType;
      productType = productType;
      if (Object.keys(packageItem!).includes('wellness')) {
        handleAutoFillPremiumGrpProducts(
          productType,
          packageItem,
          billingModeValue,
          planType,
          dateOfBirth,
          insuredSC,
          spouseSC
        );
      }
    }
    if (cancerPlan.value === 'GVCP2' || cancerPlan.value === 'GVCP3') {
      packageItem = isLoaded(packageSourceList)
        ? packageSourceList.Data.find((packageItem: object) => packageItem['packageName'] === cancerPackageName.value)
        : null;
      planType = CancerPlanType;
      productType = cancerPlan;
      if (packageItem !== undefined && packageItem !== null) {
        if (Object.keys(packageItem!).includes('cancerScreening')) {
          handleAutoFillPremiumGrpProducts(cancerPlan, packageItem, billingModeValue, planType, dateOfBirth, insuredSC);
        }
      }
    }
    if (
      accidentPlan?.value === 'GVAP1' ||
      accidentPlan?.value === 'GVAP2' ||
      accidentPlan?.value === 'GVAP6' ||
      accidentPlan?.value === 'GVA2FD' ||
      accidentPlan?.value === 'GVA1FD'
    ) {
      if (packageSourceList !== undefined) {
        packageItem = isLoaded(packageSourceList)
          ? packageSourceList.Data.find(
              (packageItem: object) => packageItem['packageName'] === accidentPackgaeName.value
            )
          : null;
        planType = accidentPlanType;
        plan = accidentPlan;

        if (Object.keys(packageItem!).includes('accTreatUrgent')) {
          handleAutoFillPremiumGrpProducts(
            accidentProductType,
            packageItem,
            billingModeValue,
            planType,
            dateOfBirth,
            insuredSC,
            undefined,
            undefined,
            undefined,
            plan
          );
        }
      }
    }
    if (shopPlan.value === 'GVSP1' || shopPlan.value === 'GVSP2') {
      if (packageSourceList !== undefined && packageSourceList !== null) {
        packageItem = isLoaded(packageSourceList)
          ? packageSourceList.Data.find((packageItem: object) => packageItem['packageName'] === shopPackageName.value)
          : null;
      }
      planType = shopPlanType;
      productType = shopPlan;
      if (
        Object.keys(packageItem!).includes('hospitalization') ||
        Object.keys(packageItem!).includes('dailyHospitalization')
      ) {
        handleAutoFillPremiumGrpProducts(productType, packageItem, billingModeValue, planType, dateOfBirth, insuredSC);
      }
    }
  };

  const handleDraftDayChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let value = parseInt(e.target.value, 10);

    if (value > 99) {
      value = 99;
    }

    if (value < 1) {
      value = 1;
    }

    dispatch(BAActions.changeDraftDay(value.toString()));
  };

  const handleChange = (event: any) => {
    setValue(event.target.value);

    if (event.target.value === 'payroll') {
      setPayrollDisabled(false);
      setPACDisabled(true);
      setPayrollPACDisabled(false);
      dispatch(BAActions.changeTransit(''));
      dispatch(BAActions.changeAccount(''));
      dispatch(BAActions.changeDraftDay(''));
      dispatch(BAActions.changePayroll(true));
      dispatch(BAActions.changeDirectBill(false));
      dispatch(BAActions.changePac(false));
    } else if (event.target.value === 'pac') {
      setPayrollDisabled(true);
      setPACDisabled(false);
      setPayrollPACDisabled(false);
      dispatch(BAActions.changeUserCaseNumber(''));
      // dispatch(BAActions.changeControlNumber(''));
      dispatch(BAActions.changeCaseName(''));
      dispatch(BAActions.changePayroll(false));
      dispatch(BAActions.changeDirectBill(false));
      dispatch(BAActions.changePac(true));
    } else if (event.target.value === 'directbill') {
      setPayrollDisabled(true);
      setPACDisabled(true);
      setPayrollPACDisabled(true);
      dispatch(BAActions.changeTransit(''));
      dispatch(BAActions.changeAccount(''));
      dispatch(BAActions.changeDraftDay(''));
      dispatch(BAActions.changePayor(''));
      dispatch(BAActions.changeState(''));
      dispatch(BAActions.changeZip(EmptyZipCode));
      dispatch(BAActions.changeUserCaseNumber(''));
      dispatch(BAActions.changeControlNumber(''));
      dispatch(BAActions.changeCaseName(''));
      dispatch(BAActions.changePayroll(false));
      dispatch(BAActions.changeDirectBill(true));
      dispatch(BAActions.changePac(false));
    }
  };

  const pacUpdate = (pac: boolean) => {
    dispatch(BAActions.changePac(pac));
    dispatch(BAActions.getAchDraftDay('1'));
    dispatch(BAActions.getAchAccountType('C'));
    if (pac.valueOf().toString() === 'true'){
      dispatch(BAActions.getAchPayerName(application.ownerInfo.ownerName.value));
    } else {
      dispatch(BAActions.getAchPayerName(''));
    }
  };

  const fetchUserData = async () => {
    if (application.caseDetails.achIndicator.valueOf().toString() === 'true' && application.policyRecord.status.value === 'SPINDEXED') {
      let ssn1 = application.policyRecord.socialSecurityNumber.value;
      const firstName1 = application.policyRecord.firstName.value;
      const lastName1 = application.policyRecord.lastName.value;
        const user = await getPACData(ssn1, firstName1, lastName1);

        if ('right' in user && user.right && user.right.pacDataResult) {
          const pacDataResultData1 = user.right.pacDataResult;
          const { transitNumber, bankAccountNumber, dueDate, accountHolderName, bankType } = pacDataResultData1;

          dispatch(BAActions.getAchTransitNumber(transitNumber));
          dispatch(BAActions.getAchAccountNumber(bankAccountNumber));
          dispatch(BAActions.getAchDraftDay(dueDate));
          dispatch(BAActions.getAchPayerName(accountHolderName));
          dispatch(BAActions.getAchAccountType(bankType));
        }

    //     dispatch(BAActions.getAchTransitNumber(transitNumber));
    //     dispatch(BAActions.getAchAccountNumber(bankAccountNumber));
    //     dispatch(BAActions.getAchDraftDay(dueDate));
    //     dispatch(BAActions.getAchPayerName(accountHolderName));
    //     dispatch(BAActions.getAchAccountType(bankType));
    }
  }; 

  useEffect(() => {
    fetchUserData();
    if (
      applicationType !== undefined &&
      handleBillingModeReload &&
      application.billingModes.key !== 'ValidBillingModes'
    ) {
      setBillingModeReload(false);
      dispatch(BAActions.getBillingModes(applicationType));
    }
    if (application.caseDetails.policyStatus.value === 'EPC') {
      dispatch(BAActions.getAchTransitNumber(application.billingAgent.transitNumber.value));
      dispatch(BAActions.getAchAccountNumber(application.billingAgent.bankAccountNumber.value));
      dispatch(BAActions.getAchDraftDay(application.billingAgent.dueDate.value));
      dispatch(BAActions.getAchPayerName(application.billingAgent.accountHolderName.value));
      dispatch(BAActions.getAchAccountType(application.billingAgent.bankType.value));
      dispatch(BAActions.changeauthorizedDate(application.billingAgent.authorizedDate));
    } else if (application.caseDetails.policyStatus.value === 'QPC' || application.caseDetails.policyStatus.value === 'RPC' || application.caseDetails.policyStatus.value === 'RPF') {
      dispatch(BAActions.getAchTransitNumber(application.billingAgent.achTransitNumber.value));
      dispatch(BAActions.getAchAccountNumber(application.billingAgent.achAccountNumber.value));
      dispatch(BAActions.getAchDraftDay(application.billingAgent.achDraftDay.value));
      dispatch(BAActions.getAchPayerName(application.billingAgent.achPayerName.value));
      dispatch(BAActions.getAchAccountType(application.billingAgent.achAccountType.value));
      dispatch(BAActions.changeauthorizedDate(application.billingAgent.achAuthorizationDate));
    }
    if(application.caseDetails.achIndicator.valueOf().toString() === 'true') {
      dispatch(BAActions.changePac(true));
      if (application.caseDetails.policyStatus.value === '') {
        dispatch(BAActions.getAchDraftDay('1'));
        dispatch(BAActions.getAchAccountType('C'));
      }
    } else if (application.caseDetails.achIndicator.valueOf().toString() === 'false' && application.billingAgent.pac.valueOf().toString() === 'false'){
      dispatch(BAActions.getAchTransitNumber(''));
      dispatch(BAActions.getAchAccountNumber(''));
      dispatch(BAActions.getAchDraftDay(''));
      dispatch(BAActions.getAchPayerName(''));
      dispatch(BAActions.getAchAccountType(''));
    }
  }, [dispatch, application.billingModes.key, applicationType, handleBillingModeReload]);

  const textInput = useRef<HTMLLabelElement | null>(null);

  return (
    <>
      <form noValidate autoComplete="off" className="billing-agent-section">
        <div className="billing-agent-section__tile-container">
          <Typography variant="subtitle1" className="billing-agent-section__tile-container-title">
            Billing &amp; Agents
          </Typography>
        </div>

        <RadioGroup aria-label="types" name="types" value={value} onChange={handleChange}>
          {BA.matchBillingAgentByType({
            Ordinary: ordinaryApp => (
              <>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      className="billing-agent-section__text-field"
                      value="payroll"
                      control={
                        <Radio
                          color="secondary"
                          checked={application.billingAgent.payroll === null ? false : application.billingAgent.payroll}
                          onChange={e => dispatch(BAActions.changePayroll(e.target.checked))}
                        />
                      }
                      label={<Typography>Payroll</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {matchCaseNumber({
                      Loading: () => (
                        <TextField
                          label="Case Number"
                          variant="filled"
                          placeholder="Case Number"
                          className="billing-agent-section__text-field"
                          value={''}
                          InputProps={{
                            endAdornment: (
                              <CircularProgress className="case-details-section__loading-policy-number-icon" />
                            ),
                          }}
                          disabled={payrollDisabled}
                        />
                      ),
                      Loaded: pn => (
                        <TextField
                          label="Case Number"
                          variant="outlined"
                          placeholder="Case Number"
                          className="billing-agent-section__text-field"
                          value={pn}
                          onChange={e => {
                            textInput.current?.focus();
                            dispatch(BAActions.changeUserCaseNumber(e.target.value as string));
                          }}
                          onBlur={e => {
                            dispatch(checkCaseNumberBillingMode(e.target.value));
                          }}
                        />
                      ),
                      Unassigned: () => (
                        <TextField
                          label="Case Number"
                          variant="filled"
                          placeholder="Case Number"
                          className="billing-agent-section__text-field"
                          value={''}
                          onChange={e => {
                            textInput.current?.focus();
                            dispatch(BAActions.changeUserCaseNumber(e.target.value as string));
                          }}
                        />
                      ),
                      UserAssignedCaseNumber: pn => (
                        <TextField
                          label="Case Number"
                          variant="outlined"
                          placeholder="Enter Case Number"
                          className="billing-agent-section__text-field"
                          value={pn}
                          margin="normal"
                          fullWidth
                          onChange={e => dispatch(BAActions.changeUserCaseNumber(e.target.value as string))}
                          onBlur={e => {
                            if (application.iwl.plan.key === 'NonEmpty') {
                              let iwlPlans: Plan[] = [];
                              let productType: Product[] = [];

                              if (
                                isValidCaseDetails(application.caseDetails) &&
                                isLoadedPolicyTypes(application.caseDetails.productType)
                              ) {
                                productType = application.caseDetails.productType.selectedPolicyTypes;
                              }

                              productType.forEach(product => {
                                if (
                                  product.productGroup!.toUpperCase() ===
                                  ProductRiderGroups[ProductsRidersPolicyType.Iwl]
                                ) {
                                  iwlPlans = product.plans!;
                                }
                              });

                              const plan: Plan = iwlPlans.find(p => p.name! === application.iwl.plan.value)!;
                              dispatch(IWLActions.getOccClass(e.target.value, plan));
                            }

                            dispatch(BAActions.checkCaseNumberBillingMode(e.target.value));
                          }}
                          id="user-assigned-billing-agent-case-number"
                          disabled={payrollDisabled}
                        />
                      ),
                      Error: () => (
                        <Alert
                          className="billing-agent-section__policy-number-error-label"
                          message="An Error Ocurred While Getting Case Number."
                        />
                      ),
                      CaseNumberError: pn => (
                        <TextField
                          error
                          id="outlined-error-helper-text"
                          label="Case Number"
                          defaultValue={pn}
                          helperText="Not a Valid Case Number"
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          value={pn}
                          onChange={e => dispatch(BAActions.changeUserCaseNumber(e.target.value as string))}
                          className="billing-agent-section__text-field"
                          placeholder="Enter Case Number"
                        />
                      ),
                    })(ordinaryApp.caseNumber)}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="control-number"
                      label="Control #"
                      variant="outlined"
                      placeholder="Enter Control #"
                      margin="normal"
                      fullWidth
                      inputProps={{maxLength: 10}}
                      value={ordinaryApp.controlNumber.value}
                      onChange={e => dispatch(BAActions.changeControlNumber(e.target.value))}
                      className="billing-agent-section__text-field"
                      disabled={payrollDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="case-name"
                      label="Case Name"
                      variant="filled"
                      placeholder="Case Name"
                      inputProps={{readOnly: true}}
                      margin="normal"
                      fullWidth
                      value={ordinaryApp.caseName.value}
                      className="billing-agent-section__text-field"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      value="pac"
                      className="billing-agent-section__text-field"
                      control={
                        <Radio
                          color="secondary"
                          checked={application.billingAgent.pac === null ? false : application.billingAgent.pac}
                          onChange={e => dispatch(BAActions.changePac(e.target.checked))}
                        />
                      }
                      label={<Typography>PAC</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="transit"
                      label="Transit"
                      variant="outlined"
                      placeholder="Enter Transit"
                      margin="normal"
                      fullWidth
                      className="billing-agent-section__text-field"
                      inputProps={{maxLength: 9}}
                      value={ordinaryApp.transit.value}
                      onChange={e => dispatch(BAActions.changeTransit(e.target.value))}
                      disabled={pacDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="account"
                      label="Account"
                      variant="outlined"
                      placeholder="Enter Account"
                      margin="normal"
                      fullWidth
                      inputProps={{maxLength: 24}}
                      value={ordinaryApp.account.value}
                      onChange={e => dispatch(BAActions.changeAccount(e.target.value))}
                      className="billing-agent-section__text-field"
                      disabled={pacDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="Draft Day"
                      label="Draft Day"
                      variant="outlined"
                      placeholder="Enter Draft Day"
                      margin="normal"
                      fullWidth
                      type="number"
                      inputProps={{min: 1, max: 99}}
                      value={ordinaryApp.draftDay.value}
                      onChange={e => handleDraftDayChange(e)}
                      className="billing-agent-section__text-field"
                      disabled={pacDisabled}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="payor"
                      label="Payor"
                      variant="outlined"
                      placeholder="Enter Payor"
                      margin="normal"
                      fullWidth
                      className="billing-agent-section__text-field"
                      value={
                        modalApplication.application.ownerInfo.applicantIsOwner ||
                        modalApplication.application.ownerInfo.ownerName.value === ''
                          ? modalApplication.application.applicantDemographics.name.value
                          : modalApplication.application.ownerInfo.ownerName.value
                      }
                      onChange={e => dispatch(BAActions.changePayor(e.target.value))}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="billing-agent-section__text-field">
                      <InputLabel ref={setStateLabel} id="select-state-label">
                        State
                      </InputLabel>
                      <Select
                        labelWidth={stateLabel?.clientWidth}
                        labelId="select-state-label"
                        id="select-state"
                        value={applicantDemographics.state.value || ordinaryApp.state.value}
                        onChange={e => dispatch(BAActions.changeState(e.target.value as string))}
                        disabled={payrollPACDisabled}
                      >
                        <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                        {stateList.map(stateCode => (
                          <MenuItem key={stateCode} value={stateCode}>
                            {stateCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      className="billing-agent-section__text-field"
                      error={showZipError}
                      helperText={showZipError ? getZipErrorMessage(ordinaryApp.zip) : ''}
                      id="billing-agent-zip-code"
                      inputProps={{maxLength: 10}}
                      label="Zip"
                      onBlur={() => setBlurZip(true)}
                      onChange={e => dispatch(BAActions.changeZip(ValidZipCode.create(unmaskZipCode(e.target.value))))}
                      placeholder="XXXXX-XXXX"
                      value={maskZipCode(ordinaryApp.zip.value)}
                      variant="outlined"
                      disabled={payrollPACDisabled}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      className="billing-agent-section__text-field"
                      value="directbill"
                      control={
                        <Radio
                          color="secondary"
                          onChange={e => dispatch(BAActions.changeDirectBill(e.target.checked))}
                        />
                      }
                      label={<Typography>Direct Bill</Typography>}
                    />
                  </Grid>
                </Grid>
              </>
            ),
            NewYork: newYork => (
              <>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      className="billing-agent-section__text-field"
                      value="payroll"
                      control={
                        <Radio
                          color="secondary"
                          checked={application.billingAgent.payroll === null ? false : application.billingAgent.payroll}
                          onChange={e => dispatch(BAActions.changePayroll(e.target.checked))}
                        />
                      }
                      label={<Typography>Payroll</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {matchCaseNumber({
                      Loading: () => (
                        <TextField
                          label="Case Number"
                          variant="filled"
                          placeholder="Case Number"
                          className="billing-agent-section__text-field"
                          value={''}
                          InputProps={{
                            endAdornment: (
                              <CircularProgress className="case-details-section__loading-policy-number-icon" />
                            ),
                          }}
                          disabled={payrollDisabled}
                        />
                      ),
                      Loaded: pn => (
                        <TextField
                          label="Case Number"
                          variant="outlined"
                          placeholder="Case Number"
                          className="billing-agent-section__text-field"
                          value={pn}
                          onChange={e => {
                            textInput.current?.focus();
                            dispatch(BAActions.changeUserCaseNumber(e.target.value as string));
                          }}
                          onBlur={e => {
                            dispatch(BAActions.getCaseNameByCaseNumber(e.target.value));
                          }}
                        />
                      ),
                      Unassigned: () => (
                        <TextField
                          label="Case Number"
                          variant="filled"
                          placeholder="Case Number"
                          className="billing-agent-section__text-field"
                          value={''}
                          onChange={e => {
                            textInput.current?.focus();
                            dispatch(BAActions.changeUserCaseNumber(e.target.value as string));
                          }}
                        />
                      ),
                      UserAssignedCaseNumber: pn => (
                        <TextField
                          label="Case Number"
                          variant="outlined"
                          placeholder="Enter Case Number"
                          className="billing-agent-section__text-field"
                          value={pn}
                          margin="normal"
                          fullWidth
                          onChange={e => dispatch(BAActions.changeUserCaseNumber(e.target.value as string))}
                          onBlur={e => dispatch(BAActions.getCaseNameByCaseNumber(e.target.value))}
                          id="user-assigned-billing-agent-case-number"
                          disabled={payrollDisabled}
                        />
                      ),
                      Error: () => (
                        <Alert
                          className="billing-agent-section__policy-number-error-label"
                          message="An Error Ocurred While Getting Case Number."
                        />
                      ),
                      CaseNumberError: pn => (
                        <TextField
                          error
                          id="outlined-error-helper-text"
                          label="Case Number"
                          defaultValue={pn}
                          helperText="Not a Valid Case Number"
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          value={pn}
                          onChange={e => dispatch(BAActions.changeUserCaseNumber(e.target.value as string))}
                          className="billing-agent-section__text-field"
                          placeholder="Enter Case Number"
                        />
                      ),
                    })(newYork.caseNumber)}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="control-number"
                      label="Control #"
                      variant="outlined"
                      placeholder="Enter Control #"
                      inputProps={{maxLength: 10}}
                      margin="normal"
                      fullWidth
                      value={newYork.controlNumber.value}
                      onChange={e => dispatch(BAActions.changeControlNumber(e.target.value))}
                      className="billing-agent-section__text-field"
                      disabled={payrollDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="case-name"
                      label="Case Name"
                      variant="filled"
                      placeholder="Case Name"
                      inputProps={{readOnly: true}}
                      margin="normal"
                      fullWidth
                      value={newYork.caseName.value}
                      className="billing-agent-section__text-field"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      value="pac"
                      className="billing-agent-section__text-field"
                      control={
                        <Radio
                          color="secondary"
                          checked={application.billingAgent.pac === null ? false : application.billingAgent.pac}
                          onChange={e => dispatch(BAActions.changePac(e.target.checked))}
                        />
                      }
                      label={<Typography>PAC</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="transit"
                      label="Transit"
                      variant="outlined"
                      placeholder="Enter Transit"
                      margin="normal"
                      fullWidth
                      inputProps={{maxLength: 9}}
                      className="billing-agent-section__text-field"
                      value={newYork.transit.value}
                      onChange={e => dispatch(BAActions.changeTransit(e.target.value))}
                      disabled={pacDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="account"
                      label="Account"
                      variant="outlined"
                      placeholder="Enter Account"
                      margin="normal"
                      fullWidth
                      inputProps={{maxLength: 24}}
                      value={newYork.account.value}
                      onChange={e => dispatch(BAActions.changeAccount(e.target.value))}
                      className="billing-agent-section__text-field"
                      disabled={pacDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="Draft Day"
                      label="Drafy Day"
                      variant="outlined"
                      placeholder="Enter Draft Day"
                      margin="normal"
                      fullWidth
                      type="number"
                      inputProps={{min: 1, max: 99}}
                      value={newYork.draftDay.value}
                      onChange={e => handleDraftDayChange(e)}
                      className="billing-agent-section__text-field"
                      disabled={pacDisabled}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="payor"
                      label="Payor"
                      variant="outlined"
                      placeholder="Enter Payor"
                      margin="normal"
                      fullWidth
                      className="billing-agent-section__text-field"
                      value={
                        modalApplication.application.ownerInfo.applicantIsOwner ||
                        modalApplication.application.ownerInfo.ownerName.value === ''
                          ? modalApplication.application.applicantDemographics.name.value
                          : modalApplication.application.ownerInfo.ownerName.value
                      }
                      onChange={e => dispatch(BAActions.changePayor(e.target.value))}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="billing-agent-section__text-field">
                      <InputLabel ref={setStateLabel} id="select-state-label">
                        State
                      </InputLabel>
                      <Select
                        labelWidth={stateLabel?.clientWidth}
                        labelId="select-state-label"
                        id="select-state"
                        value={newYork.state.value}
                        onChange={e => dispatch(BAActions.changeState(e.target.value as string))}
                        disabled={payrollPACDisabled}
                      >
                        <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                        {stateList.map(stateCode => (
                          <MenuItem key={stateCode} value={stateCode}>
                            {stateCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      className="billing-agent-section__text-field"
                      error={showZipError}
                      helperText={showZipError ? getZipErrorMessage(newYork.zip) : ''}
                      id="billing-agent-zip-code"
                      inputProps={{maxLength: 10}}
                      label="Zip"
                      onBlur={() => setBlurZip(true)}
                      onChange={e => dispatch(BAActions.changeZip(ValidZipCode.create(unmaskZipCode(e.target.value))))}
                      placeholder="XXXXX-XXXX"
                      value={newYork.zip.value}
                      variant="outlined"
                      disabled={payrollPACDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      className="billing-agent-section__text-field"
                      value="directbill"
                      control={
                        <Radio
                          color="secondary"
                          checked={
                            application.billingAgent.directBill === null ? false : application.billingAgent.directBill
                          }
                          onChange={e => dispatch(BAActions.changeDirectBill(e.target.checked))}
                        />
                      }
                      label={<Typography>Direct Bill</Typography>}
                    />
                  </Grid>
                </Grid>
              </>
            ),
            Group: () => (
              <>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      value="pac"
                      className="billing-agent-section__text-field"
                      control={
                        <Radio
                          color="secondary"
                          checked={application.billingAgent.pac === null
                            ? false
                            : ((application.caseDetails.achIndicator.valueOf().toString() === 'true') ? application.billingAgent.pac === true : application.billingAgent.pac)
                        }
                          onChange={e => pacUpdate(e.target.checked)}
                          disabled={(application.caseDetails.achIndicator.valueOf().toString() === 'false' && notACH.valueOf().toString() === 'false') || 
                          (application.caseDetails.achIndicator.valueOf().toString() === 'true' && (application.caseDetails.policyStatus.value === 'EPC' || application.caseDetails.policyStatus.value === 'QPC' || application.caseDetails.policyStatus.value === 'RPC' || application.caseDetails.policyStatus.value === 'RPF'))}
                        />
                      }
                      label={<Typography>PAC</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="Transit"
                      label="Transit "
                      variant="outlined"
                      placeholder="Enter Transit"
                      margin="normal"
                      fullWidth
                      className="billing-agent-section__text-field"
                      inputProps={{maxLength: 9}}
                      value={application.caseDetails.policyStatus.value === 'EPC' ? application.billingAgent.transitNumber.value : application.billingAgent.achTransitNumber.value}
                      onChange={e => dispatch(BAActions.getAchTransitNumber(e.target.value))}
                      disabled={
                        (((application.caseDetails.achIndicator.valueOf().toString() === 'false') || 
                        (application.caseDetails.achIndicator.valueOf().toString() === 'true' && (application.caseDetails.policyStatus.value === 'EPC' || application.caseDetails.policyStatus.value === 'QPC' || application.caseDetails.policyStatus.value === 'RPC' || application.caseDetails.policyStatus.value === 'RPF'))
                        ) ? pacDisabled : !pacDisabled)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="account"
                      label="Account"
                      variant="outlined"
                      placeholder="Enter Account"
                      margin="normal"
                      fullWidth
                      inputProps={{maxLength: 13}}
                      value={application.caseDetails.policyStatus.value === 'EPC' ? application.billingAgent.bankAccountNumber.value : application.billingAgent.achAccountNumber.value}
                      onChange={e => dispatch(BAActions.getAchAccountNumber(e.target.value))}
                      className="billing-agent-section__text-field"
                      disabled={
                        (((application.caseDetails.achIndicator.valueOf().toString() === 'false') || 
                        (application.caseDetails.achIndicator.valueOf().toString() === 'true' && (application.caseDetails.policyStatus.value === 'EPC' || application.caseDetails.policyStatus.value === 'QPC' || application.caseDetails.policyStatus.value === 'RPC' || application.caseDetails.policyStatus.value === 'RPF'))
                        ) ? pacDisabled : !pacDisabled)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Draft Day"
                      id="Draft Day"
                      variant="outlined"
                      placeholder="Enter Draft Day"
                      error={showDaYError ? showDaYError : false}
                      type="number"
                      inputProps={{min: 1, max: 31}}
                      helperText={ showDaYError ? 'Please Enter a Valid Draft Day' : ''}
                      value={application.caseDetails.policyStatus.value === 'EPC' ? application.billingAgent.dueDate.value : (application.billingAgent.achDraftDay.value === '' ? '1' : application.billingAgent.achDraftDay.value)}
                      onChange={e => dispatch(BAActions.getAchDraftDay(e.target.value))}
                      className="billing-agent-section__text-field"
                      disabled={
                        (((application.caseDetails.achIndicator.valueOf().toString() === 'false') || 
                        (application.caseDetails.achIndicator.valueOf().toString() === 'true' && (application.caseDetails.policyStatus.value === 'EPC' || application.caseDetails.policyStatus.value === 'QPC' || application.caseDetails.policyStatus.value === 'RPC' || application.caseDetails.policyStatus.value === 'RPF'))
                        )? pacDisabled : !pacDisabled)
                      }
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="payor_Name"
                      label="Payor Name"
                      variant="outlined"
                      placeholder="Enter Payor Name"
                      margin="normal"
                      fullWidth
                      className="billing-agent-section__text-field"
                      value={application.caseDetails.policyStatus.value === 'EPC'
                        ? application.billingAgent.accountHolderName.value : (application.billingAgent.achPayerName.value === ''
                          ? application.ownerInfo.ownerName.value : application.billingAgent.achPayerName.value)}
                      onChange={e => dispatch(BAActions.getAchPayerName(e.target.value))}
                      disabled={
                        (((application.caseDetails.achIndicator.valueOf().toString() === 'false') || 
                        (application.caseDetails.achIndicator.valueOf().toString() === 'true' && (application.caseDetails.policyStatus.value === 'EPC' || application.caseDetails.policyStatus.value === 'QPC' || application.caseDetails.policyStatus.value === 'RPC' || application.caseDetails.policyStatus.value === 'RPF'))
                        ) ? pacDisabled : !pacDisabled)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="applicant-demographics-section__field">
                      <InputLabel ref={setAccountTypeLabel} id="select-AccountType-label">
                        Account Type
                      </InputLabel>
                      <Select
                        labelWidth={accountTypeLabel?.clientWidth}
                        labelId="select-AccountType-label"
                        id="select-AccountType"
                        value={application.caseDetails.policyStatus.value === 'EPC' ? application.billingAgent.bankType.value : (application.billingAgent.achAccountType.value === '' ? 'C' : application.billingAgent.achAccountType.value)}
                        onChange={e => dispatch(BAActions.getAchAccountType(e.target.value as AccountType))}
                        disabled={
                          (((application.caseDetails.achIndicator.valueOf().toString() === 'false') || 
                          (application.caseDetails.achIndicator.valueOf().toString() === 'true' && (application.caseDetails.policyStatus.value === 'EPC' || application.caseDetails.policyStatus.value === 'QPC' || application.caseDetails.policyStatus.value === 'RPC' || application.caseDetails.policyStatus.value === 'RPF'))
                          ) ? pacDisabled : !pacDisabled)
                        }
                      >
                        <MenuItem value="C">Checking</MenuItem>
                        <MenuItem value="S">Savings</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            ),
            _: () => null,
          })(application.billingAgent)}

          <div className="billing-agent-section__row">
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                {productGroupName !== 'group_ci' &&
                  productGroupName !== 'group_shop' &&
                  productGroupName !== 'group_cancer' &&
                  productGroupName !== 'ul' &&
                  productGroupName !== 'gl_term' &&
                  productGroupName !== 'group_wl' &&
                  productGroupName !== 'group_acc' &&
                  productGroupName !== 'group_dis' && (
                    <FormControl variant="outlined" className="billing-agent-section__text-field">
                      <InputLabel ref={setBillingModeLabel} id="select-billing-mode-label">
                        Billing Mode
                      </InputLabel>
                      <Select
                        labelWidth={billingModeLabel?.clientWidth}
                        labelId="select-billing-mode-label"
                        id="select-billing-mode"
                        value={application.billingAgent.billingMode.value}
                        onChange={e => {
                          handleBillingMode(e);
                        }}
                      >
                        {application.billingModes.billingModes
                          .flatMap(billingMode => billingMode.occurrences)
                          .map(o => (
                            <MenuItem key={o.name} value={o.name}>
                              {o.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                {applicationType === ApplicationType.Group && (
                  <>
                    {(productGroupName === 'group_ci' ||
                      productGroupName === 'group_shop' ||
                      productGroupName === 'group_cancer' ||
                      productGroupName === 'ul' ||
                      productGroupName === 'gl_term' ||
                      productGroupName === 'group_wl' ||
                      productGroupName === 'group_acc' ||
                      productGroupName === 'group_dis') && (
                      <FormControl variant="outlined" className="billing-agent-section__text-field">
                        <InputLabel ref={setBillingModeLabel} id="select-billing-mode-label">
                          Billing Mode
                        </InputLabel>
                        <Select
                          labelWidth={billingModeLabel?.clientWidth}
                          labelId="select-billing-mode-label"
                          id="select-billing-mode"
                          value={application.billingAgent.billingMode.value || billingCode.value}
                          onChange={e => {
                            if (applicationType === ApplicationType.Group) {
                              // array.forEach(caseDetails => {});
                              if (
                                (criticalIllness as ValidGroupCriticalIllness) &&
                                packageSourceList.key !== 'UnassignedPackageModel'
                              ) {
                                handleBillingMode(e, packageSourceList);
                              }
                              if ((cancer as ValidGroupCancer) && cancerPackageList.Data) {
                                handleBillingMode(e, cancerPackageList);
                              }
                              if ((accident as ValidGroupAccident) && accidentPackageList.Data) {
                                handleBillingMode(e, accidentPackageList);
                              }
                              if ((shop as ValidGroupShop) && shopPackageList.Data) {
                                handleBillingMode(e, shopPackageList);
                              }
                            } else {
                              handleBillingMode(e);
                            }
                          }}
                        >
                          {application.billingModes.billingModes
                            .flatMap(billingMode => billingMode.occurrences)
                            .map(o => (
                              <MenuItem key={o.name} value={o.name}>
                                {o.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <KeyboardDatePicker
                  className="billing-agent-section__text-field"
                  error={showIssueDateError}
                  format="MM/dd/yyyy"
                  helperText={showIssueDateError ? ErrorMessages.INVALID_DATE : ''}
                  inputVariant="outlined"
                  label="Issue Date"
                  onBlur={() => setBlurIssueDate(true)}
                  onChange={date => {
                    dispatch(BAActions.changeIssueDate(date?.toJSDate() || ''));
                  }}
                  value={application.billingAgent.issueDate || null}
                  variant="inline"
                  maxDate={yesterday}
                  id="issue-date"
                  autoOk={true}
                />
              </Grid>

              {matchFormNumber({
                Loading: () => (
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Form Number"
                      variant="filled"
                      placeholder="Form Number"
                      className="billing-agent-section__text-field"
                      value={''}
                      InputProps={{
                        endAdornment: <CircularProgress className="case-details-section__loading-policy-number-icon" />,
                      }}
                    />
                  </Grid>
                ),
                Loaded: pn => (
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Form Number"
                      variant="outlined"
                      placeholder="Form Number"
                      className="billing-agent-section__text-field"
                      value={pn.value}
                      onChange={e => {
                        textInput.current?.focus();
                        dispatch(BAActions.changeUserFormNumber(e.target.value as string, '', '', '', ''));
                      }}
                      onBlur={e => dispatch(BAActions.validateFormNumber(e.target.value))}
                    />
                  </Grid>
                ),
                Unassigned: () => (
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Form Number"
                      variant="filled"
                      placeholder="Form Number"
                      className="billing-agent-section__text-field"
                      value={''}
                      onChange={e => {
                        textInput.current?.focus();
                        dispatch(BAActions.changeUserFormNumber(e.target.value as string, '', '', '', ''));
                      }}
                    />
                  </Grid>
                ),
                UserAssignedFormNumber: pn => (
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Form Number"
                      variant="outlined"
                      placeholder="Enter Form Number"
                      className="billing-agent-section__text-field"
                      value={pn}
                      margin="normal"
                      fullWidth
                      onChange={e => dispatch(BAActions.changeUserFormNumber(e.target.value as string, '', '', '', ''))}
                      id="user-assigned-billing-agent-case-number"
                      onBlur={e => {
                        if (!isFormNumberSpecialChar(e.target.value)) {
                          dispatch(BAActions.validateFormNumber(e.target.value));
                        }
                      }}
                      error={showSpecialCharactersinFormNumber}
                      helperText={showSpecialCharactersinFormNumber ? 'Form Number has special character' : ''}
                    />
                  </Grid>
                ),
                Error: () => (
                  <Alert
                    className="billing-agent-section__policy-number-error-label"
                    message="An Error Ocurred While Verifying Form Number."
                  />
                ),
                FormNumberError: pn => (
                  <Grid item xs={12} md={3}>
                    <TextField
                      error
                      id="outlined-error-helper-text"
                      label="Form Number"
                      defaultValue={pn}
                      helperText="Not a Valid Form Number"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      value={pn}
                      onChange={e => dispatch(BAActions.changeUserFormNumber(e.target.value as string, '', '', '', ''))}
                      className="billing-agent-section__text-field"
                      placeholder="Enter Form Number"
                    />
                  </Grid>
                ),
              })(application.billingAgent.formNumber)}
              {BA.matchBillingAgentByType({
                Ordinary: ordinaryApp => (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl variant="outlined" className="billing-agent-section__text-field">
                        <InputLabel ref={setSignatureStateLabel} id="select-signature-state-label-ord">
                          Signature State
                        </InputLabel>
                        <Select
                          labelWidth={signatureStateLabel?.clientWidth}
                          labelId="select-signature-state-label-ord"
                          id="select-signature-state-ord"
                          value={ordinaryApp.signatureState.value}
                          onChange={e => dispatch(BAActions.changeSignatureState(e.target.value as string))}
                        >
                          <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                          {stateList.map(stateCode => (
                            <MenuItem key={stateCode} value={stateCode}>
                              {stateCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                ),
                NewYork: newYork => (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl variant="outlined" className="billing-agent-section__text-field">
                        <InputLabel ref={setSignatureStateLabel} id="select-signature-state-label-new-york">
                          Signature State
                        </InputLabel>
                        <Select
                          labelWidth={signatureStateLabel?.clientWidth}
                          labelId="select-signature-state-label-new-york"
                          id="select-signature-state-new-york"
                          value={newYork.signatureState.value}
                          onChange={e => dispatch(BAActions.changeSignatureState(e.target.value as string))}
                        >
                          <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                          {stateList.map(stateCode => (
                            <MenuItem key={stateCode} value={stateCode}>
                              {stateCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                ),
                _: () => null,
              })(application.billingAgent)}
              {getApplicationType(application.billingAgent) === 'GROUP' ? (
                <Grid item xs={12} md={3}>
                  <KeyboardDatePicker
                    className="billing-agent-section__text-field"
                    error={showDateSignedError}
                    format="MM/dd/yyyy"
                    helperText={showDateSignedError ? ErrorMessages.INVALID_DATE : ''}
                    inputVariant="outlined"
                    label="Date Signed"
                    onBlur={() => setBlurDateSigned(true)}
                    onChange={date => {
                      dispatch(BAActions.changeDateSigned(date?.toJSDate() || '')) && dispatch(BAActions.changeauthorizedDate(date?.toJSDate() || ''));
                    }}
                    value={application.billingAgent.dateSigned || null}
                    variant="inline"
                    maxDate={yesterday}
                    id="date-signed"
                    autoOk={true}
                  />
                </Grid>
              ) : null}
            </Grid>
          </div>
          <div className="billing-agent-section__row">
            <Grid container spacing={4}>
              {getApplicationType(application.billingAgent) === 'ORDINARY' ||
              getApplicationType(application.billingAgent) === 'NY' ? (
                <Grid item xs={12} md={3}>
                  <KeyboardDatePicker
                    className="billing-agent-section__text-field"
                    error={showDateSignedError}
                    format="MM/dd/yyyy"
                    helperText={showDateSignedError ? ErrorMessages.INVALID_DATE : ''}
                    inputVariant="outlined"
                    label="Date Signed"
                    onBlur={() => setBlurDateSigned(true)}
                    onChange={date => {
                      dispatch(BAActions.changeDateSigned(date?.toJSDate() || ''));
                    }}
                    value={application.billingAgent.dateSigned || null}
                    variant="inline"
                    maxDate={yesterday}
                    id="date-signed"
                    autoOk={true}
                  />
                </Grid>
              ) : (
                <></>
              )}
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  className="billing-agent-section__text-field"
                  control={
                    <Checkbox
                      checked={application.billingAgent.ink === null ? false : application.billingAgent.ink}
                      onChange={() => dispatch(BAActions.changeInk(!application.billingAgent.ink))}
                      name="checkedInk"
                      color="secondary"
                    />
                  }
                  label={<Typography>Ink</Typography>}
                />
              </Grid>
              {BA.matchBillingAgentByType({
                Ordinary: ordinaryApplication => (
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      className="billing-agent-section__text-field"
                      control={
                        <Checkbox
                          checked={
                            ordinaryApplication.floridaLicense === null ? false : ordinaryApplication.floridaLicense
                          }
                          onChange={() => dispatch(BAActions.changeFloridaLicense(!ordinaryApplication.floridaLicense))}
                          name="checkedFloridaLicense"
                          color="secondary"
                          disabled={application.applicantDemographics.state.value !== 'FL'}
                        />
                      }
                      label={<Typography>FL License # Included</Typography>}
                    />
                  </Grid>
                ),
                _: () => <></>,
              })(application.billingAgent)}
              {BA.matchBillingAgentByType({
                Group: groupApp => (
                  <>
                    <Grid item xs={12} md={3}>
                      <TextField
                        id="payor"
                        label="Payor"
                        variant="outlined"
                        placeholder="Enter Payor"
                        margin="normal"
                        fullWidth
                        className="billing-agent-section__text-field"
                        value={
                          modalApplication.application.ownerInfo.applicantIsOwner ||
                          modalApplication.application.ownerInfo.ownerName.value === ''
                            ? modalApplication.application.applicantDemographics.name.value
                            : modalApplication.application.ownerInfo.ownerName.value
                        }
                        onChange={e => dispatch(BAActions.changePayor(e.target.value))}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl variant="outlined" className="billing-agent-section__text-field">
                        <InputLabel ref={setStateLabel} id="select-state-label">
                          State
                        </InputLabel>
                        <Select
                          labelWidth={stateLabel?.clientWidth}
                          labelId="select-state-label"
                          id="select-state"
                          value={application.billingAgent.state.value}
                          onChange={e => {
                            dispatch(BAActions.changeState(e.target.value as string));
                          }}
                          disabled={payrollPACDisabled}
                        >
                          <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                          {stateList.map(stateCode => (
                            <MenuItem key={stateCode} value={stateCode}>
                              {stateCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        className="billing-agent-section__text-field"
                        error={showZipError}
                        helperText={showZipError ? getZipErrorMessage(groupApp.zip) : ''}
                        id="billing-agent-zip-code"
                        inputProps={{maxLength: 10}}
                        label="Zip"
                        onBlur={() => setBlurZip(true)}
                        onChange={e =>
                          dispatch(BAActions.changeZip(ValidZipCode.create(unmaskZipCode(e.target.value))))
                        }
                        placeholder="XXXXX-XXXX"
                        value={maskZipCode(groupApp.zip.value)}
                        variant="outlined"
                      />
                    </Grid>
                  </>
                ),
                _: () => null,
              })(application.billingAgent)}
            </Grid>
          </div>

          {BA.matchBillingAgentByType({
            Group: groupApp => (
              <div className="billing-agent-section__row">
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="control-number"
                      label="Control #"
                      variant="outlined"
                      placeholder="Enter Control #"
                      margin="normal"
                      fullWidth
                      inputProps={{maxLength: 10}}
                      value={groupApp.controlNumber.value}
                      onChange={e => dispatch(BAActions.changeControlNumber(e.target.value))}
                      className="billing-agent-section__text-field"
                      // disabled={pacDisabled}
                    />
                  </Grid>
                </Grid>
              </div>
            ),
            _: () => null,
          })(application.billingAgent)}
        </RadioGroup>
      </form>
      <Agents application={application} />
      <Divider className="application-form__actions-divider" />
    </>
  );
};

export default BillingAgentSection;
